import { clamp01 } from './math';
import type { Rng } from './rng';
import {
  hslToRgb,
  mixIn,
  oklchToRgb,
  parseHex,
  rgbToHsl,
  rgbToOklch,
  toHex,
  type ColorSpace,
  type Rgb,
} from './color';

/**
 * The colour system.
 *
 * A palette is described declaratively — curated set, hand-built stop list, or
 * algorithmically derived from one base colour — and resolved into a `ResolvedPalette`
 * that techniques sample. Techniques never see hex strings or interpolation spaces;
 * they ask for "the colour at t" or "colour number i" and get bytes back.
 *
 * Everything expensive is baked once at resolve time, because a technique may ask for
 * a colour several hundred thousand times per frame.
 */

export interface Palette {
  id: string;
  name: string;
  colors: string[];
}

export const CURATED_PALETTES: Palette[] = [
  { id: 'bauhaus', name: 'Bauhaus', colors: ['#1c1c1c', '#d8352a', '#f2c14e', '#2b5ea8', '#f4f1e8'] },
  { id: 'riso', name: 'Riso', colors: ['#ff48b0', '#00a3da', '#ffe800', '#00a95c', '#1a1a1a'] },
  { id: 'neon', name: 'Neon', colors: ['#0d0221', '#ff2a6d', '#05d9e8', '#d1f7ff', '#7a04eb'] },
  { id: 'mono', name: 'Monochrom', colors: ['#0a0a0a', '#3d3d3d', '#787878', '#b8b8b8', '#f4f4f4'] },
  { id: 'pastel', name: 'Pastell', colors: ['#f7d9d9', '#f5e6c8', '#cfe3d4', '#c7d5e8', '#e0d3e8'] },
  { id: 'earth', name: 'Erdton', colors: ['#2f2416', '#7a5c3e', '#b08a5e', '#d8c3a0', '#4a5c3a'] },
  { id: 'cyberpunk', name: 'Cyberpunk', colors: ['#03000f', '#00fff0', '#ff003c', '#fcee0a', '#8a2be2'] },
  { id: 'ink', name: 'Tinte', colors: ['#08080a', '#1b2b3a', '#3d5a73', '#8fb0c4', '#e8eef2'] },
  { id: 'ember', name: 'Glut', colors: ['#140b06', '#5c1a0b', '#c03a12', '#f28c28', '#ffd79a'] },
  { id: 'moss', name: 'Moos', colors: ['#0f1a0f', '#2d4a26', '#5c8a3c', '#a3c65c', '#e3f0c0'] },
  { id: 'acid', name: 'Säure', colors: ['#0b0b0e', '#d8ff3e', '#57e389', '#2b6cff', '#ff7a3c'] },
  { id: 'blueprint', name: 'Blaupause', colors: ['#0a2540', '#123f6b', '#2e7fbf', '#8ec7f0', '#ffffff'] },
];

export const PALETTE_BY_ID = new Map(CURATED_PALETTES.map((p) => [p.id, p]));

// ── Generative palettes ──────────────────────────────────────────────────────

export type HarmonyMode = 'complementary' | 'triad' | 'analogous' | 'splitComplementary' | 'tetradic' | 'mono';

export const HARMONY_LABELS: Record<HarmonyMode, string> = {
  complementary: 'Komplementär',
  triad: 'Triade',
  analogous: 'Analog',
  splitComplementary: 'Split-Komplementär',
  tetradic: 'Tetrade',
  mono: 'Monochrom',
};

/** Hue offsets in turns, relative to the base colour. */
const HARMONY_OFFSETS: Record<HarmonyMode, number[]> = {
  complementary: [0, 0.5],
  triad: [0, 1 / 3, 2 / 3],
  analogous: [-1 / 12, 0, 1 / 12, 1 / 6],
  splitComplementary: [0, 5 / 12, 7 / 12],
  tetradic: [0, 0.25, 0.5, 0.75],
  mono: [0],
};

/**
 * Derives a palette from one base colour.
 *
 * Hue relationships are applied in HSL because that is where "complementary" and
 * "triad" are defined, but the lightness and chroma spread is applied in OKLCH: an
 * even spread of HSL lightness produces wildly uneven *perceived* brightness, which
 * makes half a generated palette unusable.
 */
export function generatePalette(
  base: string,
  harmony: HarmonyMode,
  count: number,
  satSpread: number,
  lightSpread: number,
  rng: Rng,
): string[] {
  const baseHsl = rgbToHsl(parseHex(base));
  const offsets = HARMONY_OFFSETS[harmony];
  const out: string[] = [];

  for (let i = 0; i < count; i++) {
    const offset = offsets[i % offsets.length];
    // Later colours drift a little further, so a five-stop triad is not just the same
    // three hues twice.
    const drift = Math.floor(i / offsets.length) * 0.035 * (i % 2 === 0 ? 1 : -1);
    const h = (baseHsl.h + offset + drift + 1) % 1;

    const s = clamp01(baseHsl.s + rng.signed(satSpread));
    const rough = hslToRgb({ h, s, l: baseHsl.l });

    // Spread perceptual lightness evenly across the set, then jitter.
    const lch = rgbToOklch(rough);
    const span = count > 1 ? (i / (count - 1)) * 2 - 1 : 0;
    const L = clamp01(lch.L + span * lightSpread + rng.signed(lightSpread * 0.25));
    out.push(toHex(oklchToRgb({ L, C: lch.C, h: lch.h })));
  }

  return out;
}

// ── Palette specification (the value of a `palette` parameter) ───────────────

export type PaletteSource = 'curated' | 'custom' | 'generative';

export interface PaletteSpec {
  source: PaletteSource;
  /** Curated set id. */
  paletteId: string;
  /** Hand-built stop list. */
  colors: string[];
  /** Generative: base colour and harmony. */
  base: string;
  harmony: HarmonyMode;
  count: number;
  satSpread: number;
  lightSpread: number;
  /** Interpolation space for continuous sampling. */
  space: ColorSpace;
  reverse: boolean;
}

export const DEFAULT_PALETTE: PaletteSpec = {
  source: 'curated',
  paletteId: 'acid',
  colors: ['#d8ff3e', '#2b6cff'],
  base: '#d8ff3e',
  harmony: 'splitComplementary',
  count: 5,
  satSpread: 0.15,
  lightSpread: 0.22,
  space: 'oklch',
  reverse: false,
};

export function clonePalette(spec: PaletteSpec): PaletteSpec {
  return { ...spec, colors: [...spec.colors] };
}

/** Normalises anything loaded from a preset or a share link. */
export function coercePalette(value: unknown): PaletteSpec {
  if (!value || typeof value !== 'object') return clonePalette(DEFAULT_PALETTE);
  const v = value as Partial<PaletteSpec>;
  return {
    source: v.source === 'custom' || v.source === 'generative' ? v.source : 'curated',
    paletteId: PALETTE_BY_ID.has(v.paletteId ?? '') ? (v.paletteId as string) : DEFAULT_PALETTE.paletteId,
    colors: Array.isArray(v.colors) && v.colors.length > 0 ? v.colors.slice(0, 32) : [...DEFAULT_PALETTE.colors],
    base: typeof v.base === 'string' ? v.base : DEFAULT_PALETTE.base,
    harmony: v.harmony && v.harmony in HARMONY_LABELS ? v.harmony : DEFAULT_PALETTE.harmony,
    count: Math.max(1, Math.min(12, Math.round(v.count ?? DEFAULT_PALETTE.count))),
    satSpread: clamp01(v.satSpread ?? DEFAULT_PALETTE.satSpread),
    lightSpread: clamp01(v.lightSpread ?? DEFAULT_PALETTE.lightSpread),
    space: v.space === 'rgb' || v.space === 'hsl' ? v.space : 'oklch',
    reverse: v.reverse === true,
  };
}

/** The concrete stop list a spec resolves to, before baking. */
export function paletteColors(spec: PaletteSpec, rng: Rng): string[] {
  let colors: string[];
  switch (spec.source) {
    case 'custom':
      colors = spec.colors.length ? spec.colors : [...DEFAULT_PALETTE.colors];
      break;
    case 'generative':
      colors = generatePalette(spec.base, spec.harmony, spec.count, spec.satSpread, spec.lightSpread, rng);
      break;
    default:
      colors = PALETTE_BY_ID.get(spec.paletteId)?.colors ?? DEFAULT_PALETTE.colors;
  }
  return spec.reverse ? [...colors].reverse() : colors;
}

// ── Resolved palette ─────────────────────────────────────────────────────────

const RAMP_STEPS = 256;

export interface ResolvedPalette {
  /** The discrete stops, in order. */
  colors: Rgb[];
  hex: string[];
  /** Baked continuous ramp as packed RGB bytes. */
  ramp: Uint8Array;
  /** Continuous sample in [0, 1] as a canvas colour string. */
  at(t: number, alpha?: number): string;
  /** Discrete stop by index, wrapping. */
  stop(i: number, alpha?: number): string;
  readonly length: number;
}

/**
 * Bakes a spec into something a hot loop can use.
 *
 * The ramp is a 256-step byte table: an OKLCH round trip costs a cube root and two
 * transcendentals, which at a few hundred thousand elements per frame would dominate
 * everything else the technique does.
 */
export function resolvePalette(spec: PaletteSpec, rng: Rng): ResolvedPalette {
  const hex = paletteColors(spec, rng);
  const colors = hex.map(parseHex);
  const ramp = new Uint8Array(RAMP_STEPS * 3);

  const segments = Math.max(1, colors.length - 1);
  for (let i = 0; i < RAMP_STEPS; i++) {
    const t = (i / (RAMP_STEPS - 1)) * segments;
    const idx = Math.min(segments - 1, Math.floor(t));
    const local = t - idx;
    const c = colors.length === 1 ? colors[0] : mixIn(spec.space, colors[idx], colors[idx + 1], local);
    ramp[i * 3] = Math.round(clamp01(c.r) * 255);
    ramp[i * 3 + 1] = Math.round(clamp01(c.g) * 255);
    ramp[i * 3 + 2] = Math.round(clamp01(c.b) * 255);
  }

  // Stop strings are precomputed too — techniques colouring by index would otherwise
  // build a string per element.
  const stopCache: string[] = colors.map(
    (c) => `rgb(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)})`,
  );

  return {
    colors,
    hex,
    ramp,
    length: colors.length,
    at(t, alpha = 1) {
      const i = Math.min(RAMP_STEPS - 1, Math.max(0, Math.round(clamp01(t) * (RAMP_STEPS - 1)))) * 3;
      return alpha >= 1
        ? `rgb(${ramp[i]},${ramp[i + 1]},${ramp[i + 2]})`
        : `rgba(${ramp[i]},${ramp[i + 1]},${ramp[i + 2]},${alpha.toFixed(3)})`;
    },
    stop(i, alpha = 1) {
      const idx = ((i % colors.length) + colors.length) % colors.length;
      if (alpha >= 1) return stopCache[idx];
      const c = colors[idx];
      return `rgba(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)},${alpha.toFixed(3)})`;
    },
  };
}

/** Reads a colour out of a baked ramp without allocating a string. */
export function rampRgb(ramp: Uint8Array, t: number, out: [number, number, number]): [number, number, number] {
  const steps = ramp.length / 3;
  const i = Math.min(steps - 1, Math.max(0, Math.round(clamp01(t) * (steps - 1)))) * 3;
  out[0] = ramp[i];
  out[1] = ramp[i + 1];
  out[2] = ramp[i + 2];
  return out;
}
